import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { Heart, MessageCircle, Flag, Send } from "lucide-react";
import { Link } from "react-router-dom";

const CATS = [
  { key: "", label: "All" },
  { key: "story", label: "Stories" },
  { key: "tip", label: "Tips" },
  { key: "warning", label: "Scam Warnings" },
  { key: "question", label: "Questions" },
];

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "story", tags: "" });
  const [commentText, setCommentText] = useState({});

  const load = async () => {
    const { data } = await api.get("/community/posts", { params: cat ? { category: cat } : {} });
    setPosts(data.posts || []);
  };
  useEffect(()=>{ load(); }, [cat]);

  const create = async () => {
    if (!user) return toast.error("Log in to post");
    try {
      await api.post("/community/posts", { ...form, tags: form.tags.split(",").map(t=>t.trim()).filter(Boolean) });
      setForm({ title:"", body:"", category:"story", tags:"" });
      setOpenNew(false); load();
      toast.success("Posted!");
    } catch(e){ toast.error(formatApiError(e)); }
  };

  const like = async (id) => {
    if (!user) return toast.error("Log in to like");
    try { await api.post(`/community/posts/${id}/like`); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const comment = async (id) => {
    if (!user) return toast.error("Log in to comment");
    const body = commentText[id];
    if (!body) return;
    try {
      await api.post(`/community/posts/${id}/comments`, { body });
      setCommentText({ ...commentText, [id]: "" });
      load();
    } catch(e){ toast.error(formatApiError(e)); }
  };

  const report = async (post_id) => {
    if (!user) return toast.error("Log in first");
    const reason = prompt("Why are you reporting this post?");
    if (!reason) return;
    try { await api.post("/reports", { target_type:"post", target_id:post_id, reason }); toast.success("Reported"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  return (
    <div className="nh-container py-10">
      <Toaster position="top-center"/>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl">Youth Community</h1>
          <p className="text-[var(--nh-muted)] mt-1">Share your stories, tips, and scam warnings. Moderated for safety.</p>
        </div>
        {user ? (
          <button onClick={()=>setOpenNew(true)} className="nh-btn nh-btn-primary text-xs" data-testid="new-post-btn">+ New Post</button>
        ) : (
          <Link to="/login" className="nh-btn nh-btn-primary text-xs">Log in to post</Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        {CATS.map(c=>(
          <button key={c.key} onClick={()=>setCat(c.key)} className={`nh-btn text-xs ${cat===c.key ? "nh-btn-secondary" : "nh-btn-ghost"}`} data-testid={`cat-${c.key||"all"}`}>{c.label}</button>
        ))}
      </div>

      {openNew && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="nh-card bg-white w-full max-w-xl p-6">
            <h2 className="font-display font-black text-2xl">New Post</h2>
            <div className="grid gap-3 mt-4">
              <select className="nh-input" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})}>
                {CATS.filter(c=>c.key).map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <input placeholder="Title" className="nh-input" value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} data-testid="new-post-title"/>
              <textarea placeholder="Share your story…" rows={5} className="nh-input" value={form.body} onChange={(e)=>setForm({...form, body:e.target.value})} data-testid="new-post-body"/>
              <input placeholder="Tags (comma-separated)" className="nh-input" value={form.tags} onChange={(e)=>setForm({...form, tags:e.target.value})}/>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={()=>setOpenNew(false)} className="nh-btn nh-btn-ghost text-xs">Cancel</button>
              <button onClick={create} className="nh-btn nh-btn-primary text-xs" data-testid="new-post-submit">Post</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        {posts.length === 0 && <p className="text-sm text-[var(--nh-muted)]">No posts yet in this category.</p>}
        {posts.map(p=>(
          <article key={p.id} className="nh-card p-6" data-testid={`post-${p.id}`}>
            <div className="flex items-center justify-between">
              <span className="nh-tag bg-[var(--nh-accent)]">{p.category}</span>
              <span className="text-xs text-[var(--nh-muted)]">{new Date(p.created_at).toLocaleDateString()}</span>
            </div>
            <h3 className="font-display font-black text-xl mt-2">{p.title}</h3>
            <p className="text-sm mt-2 text-[var(--nh-muted)] whitespace-pre-line">{p.body}</p>
            <p className="text-xs mt-3">— <span className="font-bold">{p.author_name}</span> <span className="text-[var(--nh-muted)]">({p.author_role})</span></p>
            {(p.tags||[]).length > 0 && <div className="flex gap-2 mt-3 flex-wrap">{p.tags.map(t=><span key={t} className="nh-tag bg-white">#{t}</span>)}</div>}

            <div className="flex items-center gap-3 mt-4 border-t-2 border-black pt-3">
              <button onClick={()=>like(p.id)} className="nh-btn nh-btn-ghost text-xs" data-testid={`like-${p.id}`}>
                <Heart size={14} className={user && p.likes?.includes(user.id) ? "fill-[var(--nh-primary)] text-[var(--nh-primary)]" : ""}/> {p.likes?.length || 0}
              </button>
              <span className="text-xs"><MessageCircle size={14} className="inline mr-1"/>{p.comments?.length || 0}</span>
              <button onClick={()=>report(p.id)} className="nh-btn nh-btn-ghost text-xs ml-auto" data-testid={`report-post-${p.id}`}>
                <Flag size={14}/> Report
              </button>
            </div>

            {(p.comments||[]).slice(-3).map(c=>(
              <div key={c.id} className="mt-2 text-sm border-l-2 border-black pl-3">
                <b>{c.author_name}</b>: {c.body}
              </div>
            ))}
            {user && (
              <div className="flex gap-2 mt-3">
                <input value={commentText[p.id]||""} onChange={(e)=>setCommentText({...commentText, [p.id]: e.target.value})} placeholder="Add a comment…" className="nh-input flex-1" data-testid={`comment-input-${p.id}`}/>
                <button onClick={()=>comment(p.id)} className="nh-btn nh-btn-secondary text-xs" data-testid={`comment-send-${p.id}`}><Send size={14}/></button>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
